/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo, useState } from 'react'
import { DateTime } from 'luxon'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Box, Button, Theme } from '@nexigendigital/nxui'
import { useAppSelector } from 'store/hooks'
import { parseISO } from 'helpers/utils'

/**********************************************************************************************************
 *   LIGHTBOXES
 **********************************************************************************************************/
import ChangeEmailAddressLightbox from 'containers/account/lightbox/changeEmailAddressLightbox'
import EditProfileLightbox from 'containers/account/lightbox/editProfileLightbox'

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
import styled from 'styled-components'

const Detail = {
    Box: styled(Box)``,
    Footer: styled.div`
        display: flex;
        justify-content: space-between;
        width: 100%;
        padding: 10px 10px 10px 0;
        border-top: 1px solid ${props => (props.theme['border--000'])};
        background-color: ${props => (props.theme['background--100'])};
        border-radius: 0 0 16px 16px;
    `,
    Updated: styled.p`
        display: flex;
        align-items: center;
        margin-left: 20px;
        font-size: 0.75rem;
        color: ${props => (props.theme['text--100'])};
        ${Theme.Functions.setFont('Inter', 600)};
    `,
    Email: {
        Container: styled.div`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        `,
        Address: styled.p`
            word-break: break-word;
        `,
        Action: styled(Anchor)`
            margin-left: 10px;
        `
    }
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Details() {
    const [lightbox, setLightbox] = useState({ status: false, template: '' })
    const { appSession: { user, account } } = useAppSelector((state) => state.app)
    const personalData = useMemo(() => {
        const {
            firstname,
            lastname,
            email,
            phone,
            date_of_birth: dateOfBirth,
            address: { address1, address2, city, state, country, postcode }
        } = user!

        return [
            [
                {
                    title: 'First Name',
                    render: firstname
                },
                {
                    title: 'Last Name',
                    render: lastname
                }
            ],
            [
                {
                    title: 'Phone Number',
                    render: phone
                },
                {
                    title: 'Date of Birth',
                    render: dateOfBirth
                }
            ],
            [
                {
                    title: 'Email Address',
                    render: (
                        <Detail.Email.Container>
                            <Detail.Email.Address>{email}</Detail.Email.Address>
                            <Detail.Email.Action
                                color={'select'}
                                onClick={() => setLightbox({ status: true, template: 'email' })}
                            >
                                Edit
                            </Detail.Email.Action>
                        </Detail.Email.Container>
                    )
                },
                {
                    title: 'Company Name',
                    render: account?.company_name
                }
            ],
            [
                {
                    title: 'Address Line 1',
                    render: address1
                },
                {
                    title: 'Address Line 2',
                    render: address2
                }
            ],
            [
                {
                    title: 'Suburb',
                    render: city
                },
                {
                    title: 'Country',
                    render: country === 'AU' ? 'Australia' : 'New Zealand'
                }
            ],
            [
                {
                    title: 'State',
                    render: state
                },
                {
                    title: 'Postcode',
                    render: postcode
                }
            ]
        ]
    }, [user])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <>
            <Detail.Box
                header={{ title: 'Profile' }}
                grid={personalData}
                footer={
                    <Detail.Footer>
                        <Detail.Updated>
                            {user?.updated_at &&
                                `Last Updated: ${parseISO(user.updated_at).setLocale('en-AU').toLocaleString(DateTime.DATETIME_FULL)}`
                            }
                        </Detail.Updated>
                        <Button type={'button'} color={'primary'} onClick={() => setLightbox({ status: true, template: 'details' })}>
                            Edit
                        </Button>
                    </Detail.Footer>
                }
            />
            <ChangeEmailAddressLightbox
                status={lightbox.status && lightbox.template === 'email'}
                close={() => setLightbox({ status: false, template: '' })}
            />
            <EditProfileLightbox
                status={lightbox.status && lightbox.template === 'details'}
                close={() => setLightbox({ status: false, template: '' })}
            />
        </>
    )
}
